import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import type {
  ParcelCreateInput,
  ParcelDto,
  ParcelListQuery,
  ParcelListResponse,
  ParcelUpdateInput,
} from '@forest/contracts';
import { api } from '@/lib/api-client';

/**
 * Error classes for geometry-editor save flow. Exposed so the editor can
 * render the correct dialog (stale record reload vs. validation banner).
 */
export class StaleParcelError extends Error {
  readonly code = 'stale' as const;
  readonly current: ParcelDto | null;
  constructor(current: ParcelDto | null, message = 'Parcel was modified since the editor was opened') {
    super(message);
    this.name = 'StaleParcelError';
    this.current = current;
  }
}

export class InvalidGeometryError extends Error {
  readonly code = 'invalid_geometry' as const;
  readonly reason: string;
  constructor(reason: string, message = 'Invalid geometry') {
    super(message);
    this.name = 'InvalidGeometryError';
    this.reason = reason;
  }
}

type ListParams = Partial<ParcelListQuery> & {
  /** Skip heavy JSONB `geometry` / `roads` on the wire. Callers that
   *  don't need per-row map / editor rendering (FilterPage results
   *  table) should set this to save ~2.5 MB at limit=500. */
  lite?: boolean;
  /** true = only parcels with subpolygons, false = only without. */
  hasSubParcels?: boolean;
};

function toSearchParams(q: ListParams): URLSearchParams {
  const p = new URLSearchParams();
  if (q.page !== undefined) p.set('page', String(q.page));
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.raion) p.set('raion', q.raion);
  // CSV multi-select params: pass empty string through ("— Neasignat" only)
  // so the server's parseCsv sees the empty slot and applies includeNull.
  if (q.raioane !== undefined) p.set('raioane', q.raioane);
  if (q.primarie) p.set('primarie', q.primarie);
  if (q.primarii !== undefined) p.set('primarii', q.primarii);
  if (q.ocolSilvic) p.set('ocolSilvic', q.ocolSilvic);
  if (q.ocoaleSilvice !== undefined) p.set('ocoaleSilvice', q.ocoaleSilvice);
  if (q.entitateSilvica) p.set('entitateSilvica', q.entitateSilvica);
  if (q.entitatiSilvice !== undefined) p.set('entitatiSilvice', q.entitatiSilvice);
  if (q.anul !== undefined) p.set('anul', String(q.anul));
  if (q.years !== undefined) p.set('years', q.years);
  if (q.anulMin !== undefined) p.set('anulMin', String(q.anulMin));
  if (q.anulMax !== undefined) p.set('anulMax', String(q.anulMax));
  if (q.codProiect) p.set('codProiect', q.codProiect);
  if (q.codProiecte !== undefined) p.set('codProiecte', q.codProiecte);
  if (q.ucf) p.set('ucf', q.ucf);
  if (q.ucfs !== undefined) p.set('ucfs', q.ucfs);
  if (q.suprafataMin !== undefined) p.set('suprafataMin', String(q.suprafataMin));
  if (q.suprafataMax !== undefined) p.set('suprafataMax', String(q.suprafataMax));
  if (q.search) p.set('search', q.search);
  if (q.includeDeleted) p.set('includeDeleted', 'true');
  if (q.sort) p.set('sort', q.sort);
  if (q.order) p.set('order', q.order);
  if (q.lite) p.set('lite', 'true');
  if (q.hasSubParcels !== undefined) p.set('hasSubParcels', String(q.hasSubParcels));
  return p;
}

export function useParcels(params: ListParams) {
  return useQuery<ParcelListResponse>({
    queryKey: ['parcels', params],
    queryFn: async () =>
      api
        .get('parcels', { searchParams: toSearchParams(params) })
        .json<ParcelListResponse>(),
    placeholderData: (prev) => prev,
  });
}

export type ParcelFacets = {
  raioane: string[];
  ocoaleSilvice: string[];
  hasNullOcolSilvic?: boolean;
  ani: number[];
  entitateSilvice: string[];
  hasNullEntitateSilvica?: boolean;
  primarii: string[];
  codProiecte: string[];
  ucfs: string[];
};

export function useParcelFacets() {
  return useQuery<ParcelFacets>({
    queryKey: ['parcels', 'facets'],
    queryFn: async () =>
      api.get('parcels/facets').json<ParcelFacets>(),
    staleTime: 5 * 60_000,
  });
}

export type MapContextParcel = {
  id: string;
  raion: string;
  primarie: string;
  ucf: string;
  anul: number;
  geometryType: string;
  geometry: unknown;
};

/**
 * All live parcel polygons, globally (not scope-filtered). Used by the
 * geometry editor to paint existing polygons as a backdrop so the user
 * can align / avoid overlap with neighbouring parcels regardless of
 * which user owns them.
 */
export type MapContextResponse = {
  items: MapContextParcel[];
  /** True when the database holds more polygons than the endpoint returns,
   *  i.e. the backdrop is incomplete and the editor must say so. */
  truncated?: boolean;
  limit?: number;
};

export function useMapContextParcels() {
  return useQuery<MapContextResponse>({
    queryKey: ['parcels', 'map-context'],
    queryFn: async () => api.get('parcels/map-context').json<MapContextResponse>(),
    staleTime: 5 * 60_000,
  });
}

export type NearestParcel = {
  distanceKm: number;
  contains: boolean;
  raion: string;
  primarie: string;
  entitateSilvica: string | null;
  ocolSilvic: string | null;
  ucf: string;
};

/**
 * Look up the nearest parcel to a point. Used by the editor autofill
 * to seed raion / primărie / entitate silvică / ocol silvic from the
 * ground-truth values already stored in neighbouring parcels. Falls
 * back to `null` on network error so the caller can quietly continue
 * with other sources (Nominatim, user profile).
 */
export async function fetchNearestParcel(
  lat: number,
  lon: number,
  signal?: AbortSignal,
): Promise<NearestParcel | null> {
  try {
    const sp = new URLSearchParams({ lat: String(lat), lon: String(lon) });
    const res = await api
      .get('parcels/nearest', { searchParams: sp, signal })
      .json<{ nearest: NearestParcel | null }>();
    return res.nearest;
  } catch {
    return null;
  }
}

/**
 * Fetch a single parcel by id — used by the per-row Harta / Imagine
 * modal in ParcelsPage. The list endpoint strips `geometry` and
 * `roads` to keep payloads small at 500+ rows; this hook refetches
 * those heavy JSONB fields on demand when the modal opens.
 */
export function useParcelDetail(id: string | null) {
  return useQuery<ParcelDto>({
    queryKey: ['parcels', 'detail', id],
    queryFn: async () => api.get(`parcels/${id}`).json<ParcelDto>(),
    enabled: !!id,
    staleTime: 60_000,
  });
}

/**
 * Count of live (non-deleted) parcels visible to the current user's
 * scope. Used to auto-generate the next `projectId` on create. We
 * piggy-back on the list endpoint with `limit=1` so the payload stays
 * tiny; only `pagination.total` is read.
 */
export function useParcelCount() {
  return useQuery<number>({
    queryKey: ['parcels', 'count'],
    queryFn: async () => {
      const res = await api
        .get('parcels', {
          searchParams: new URLSearchParams({ page: '1', limit: '1' }),
        })
        .json<ParcelListResponse>();
      return res.pagination.total;
    },
    staleTime: 30_000,
  });
}

/**
 * Every parcel mutation that touches geometry re-runs point-in-polygon on
 * photos inside the affected bbox (same transaction, server-side). Without
 * invalidating the photos cache too, the UI keeps showing the old
 * polygonStatus / parcelId — most visibly: photos that were NOT_ASSIGNED
 * before the new parcel was drawn still appear red even though the server
 * has already linked them.
 */
function invalidateParcelsAndPhotos(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['parcels'] });
  void qc.invalidateQueries({ queryKey: ['photos'] });
}

/**
 * Soft-delete a parcel. Invalidates every parcels-list query on success so
 * pagination, totals, and facet dropdowns re-fetch.
 */
export function useDeleteParcel() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.delete(`parcels/${id}`);
    },
    onSuccess: () => invalidateParcelsAndPhotos(qc),
  });
}

/**
 * Permanently delete an already-soft-deleted parcel. SUPER_ADMIN only;
 * the API returns 403 for everyone else. Irrevocable — clears linked
 * comments and ParcelInvestment rows in the same transaction.
 */
export function useHardDeleteParcel() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.delete(`parcels/${id}/permanent`);
    },
    onSuccess: () => invalidateParcelsAndPhotos(qc),
  });
}

/**
 * Restore a soft-deleted parcel. Backend rejects with 409 if a live parcel
 * with the same projectUcfKey already exists (see partial unique index).
 */
export function useRestoreParcel() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.post(`parcels/${id}/restore`);
    },
    onSuccess: () => invalidateParcelsAndPhotos(qc),
  });
}

export function useCreateParcel() {
  const qc = useQueryClient();
  return useMutation<ParcelDto, Error, ParcelCreateInput>({
    mutationFn: async (input) =>
      api.post('parcels', { json: input }).json<ParcelDto>(),
    onSuccess: () => invalidateParcelsAndPhotos(qc),
  });
}

export function useUpdateParcel() {
  const qc = useQueryClient();
  return useMutation<ParcelDto, Error, { id: string; input: ParcelUpdateInput }>({
    mutationFn: async ({ id, input }) => {
      try {
        return await api
          .patch(`parcels/${id}`, { json: input })
          .json<ParcelDto>();
      } catch (e) {
        if (e instanceof HTTPError) {
          if (e.response.status === 409) {
            const body = await safeJson(e.response);
            const current = (body?.error?.current ?? null) as ParcelDto | null;
            throw new StaleParcelError(current, body?.error?.message);
          }
          if (e.response.status === 422) {
            const body = await safeJson(e.response);
            throw new InvalidGeometryError(
              body?.error?.message ?? 'Invalid geometry',
              body?.error?.message ?? 'Invalid geometry',
            );
          }
        }
        throw e;
      }
    },
    onSuccess: () => invalidateParcelsAndPhotos(qc),
  });
}

async function safeJson(
  res: Response,
): Promise<{ error?: { code?: string; message?: string; current?: unknown } } | null> {
  try {
    return (await res.json()) as {
      error?: { code?: string; message?: string; current?: unknown };
    };
  } catch {
    return null;
  }
}
